import { createActivity } from 'store/ui/activities';
import { ActionPayload, Thunk, ThunkContext, ThunkDispatch } from 'store/types';
import { DeepPartial } from 'types/index';

import {
	GetSubscriptionAction,
	ActionTypes,
	SubscriptionUsersById,
	AddSubscriptionUserAction,
	RemoveSubscriptionUserAction,
	GetAccountAction,
	UpdateAccountAction,
	AccountDetails,
	GetLicenseModelsAction,
	ChangeUserLicenseModelAction,
	CancelUserSubscriptionInvitationAction,
	SetSubscriptionUsersSearchTermAction,
	SubscriptionUser,
	SubscriptionDetails,
	GetSubscriptionApiKeyAction,
	UserLicenseModel,
	LicenseModels,
	SubscriptionName
} from './types';
import { setOrganizationAccessRightsAction } from '../enterprise';
import { getMessageFromError, unknownErrorMessage } from 'store/utils';

//  -- ACCOUNT DETAILS --

export const getAccountAction = (payload: ActionPayload<GetAccountAction>): GetAccountAction => ({
	type: ActionTypes.GET_ACCOUNT,
	payload
});

export const getAccount = (): Thunk => async (dispatch, _, context) => {
	const activity = createActivity({
		type: ActionTypes.GET_ACCOUNT,
		dispatch
	});

	let errorMessage = null;
	const startTime = new Date().getTime();

	do {
		try {
			if (!errorMessage) activity.begin();
			await fetchAccount(dispatch, context);
			// in case it succeeds, set errorMessage to null and don't retry
			errorMessage = null;
		} catch (e: any) {
			errorMessage = getMessageFromError(e);
			// wait one second before retrying
			await new Promise(resolve => setTimeout(resolve, 1000));
		}
	} while (new Date().getTime() - startTime < 20000 && errorMessage);

	activity.end();
	if (errorMessage)
		activity.error({
			error: errorMessage,
			toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
		});
};

export const fetchAccount = async (dispatch: ThunkDispatch, context: ThunkContext) => {
	const { user: userData } = await context.api.account.subscription().getAccount();

	// Language is now owned by the shell app and synced via the communication
	// bridge, so this thunk no longer needs to read `userPreferences.chosenLanguageCode`
	// or dispatch any language change.

	// handling naming difference in license/license
	const { licenseModel, organization, ...userDataRest } = userData;

	dispatch(
		getAccountAction({
			account: {
				...userDataRest,
				licenseModel: licenseModel
			}
		})
	);
};

const updateAccountAction = (payload: ActionPayload<UpdateAccountAction>): UpdateAccountAction => ({
	type: ActionTypes.UPDATE_ACCOUNT,
	payload
});

export const updateAccount =
	(accountDetails: DeepPartial<AccountDetails>, isUserActivating?: boolean): Thunk =>
	async (dispatch, getState, context) => {
		const activity = createActivity({
			type: ActionTypes.UPDATE_ACCOUNT,
			dispatch
		});

		try {
			activity.begin();

			const { licenseModel, ...accountRest } = accountDetails;

			// handling naming difference in license/license
			let accountToStore;
			if (licenseModel) accountToStore = { licenseModel: licenseModel, ...accountRest };
			else accountToStore = accountRest;

			await context.api.account
				.subscription()
				.updateAccount({ ...accountToStore }, isUserActivating);

			dispatch(updateAccountAction({ accountDetails }));
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			activity.error({
				error: errorMessage,
				toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
			});
		} finally {
			activity.end();
		}
	};

//  -- SUBSCRIPTION --

export const getSubscriptionAction = (
	payload: ActionPayload<GetSubscriptionAction>
): GetSubscriptionAction => ({
	type: ActionTypes.GET_SUBSCRIPTION,
	payload
});

export const getSubscription = (): Thunk => async (dispatch, _, context) => {
	const activity = createActivity({
		type: ActionTypes.GET_SUBSCRIPTION,
		dispatch
	});

	try {
		activity.begin();

		const {
			coveredUsers,
			subscriptionDetails: apiSubscriptionDetails,
			isNonSubscriber,
			hasMetadataDefinition
		} = await context.api.account.subscription().getSubscription();

		const subscriptionUsersById: SubscriptionUsersById = {};
		if (coveredUsers) {
			coveredUsers.forEach(user => {
				const { licenseModel, ...apiUser } = user;
				const coveredUser: SubscriptionUser = {
					...apiUser,
					licenseModel: licenseModel
				};
				subscriptionUsersById[user.userid] = coveredUser;
			});
		}

		// For subscription bundle development
		// const mockLicenseQuantities = {
		// 	'ledidi-collaborator-license-EUR-Yearly': 10,
		// 	'ledidi-prime-license-EUR-Yearly': 5
		// };

		// const mockAvailableLicenseQuantities = {
		// 	'ledidi-collaborator-license-EUR-Yearly': 3,
		// 	'ledidi-prime-license-EUR-Yearly': 4
		// };

		const subscriptionDetails: SubscriptionDetails | null = apiSubscriptionDetails
			? {
					isPayedMonthly: apiSubscriptionDetails.isPayedMonthly,
					refundableCredits: apiSubscriptionDetails.refundableCredits,
					subscriptionId: apiSubscriptionDetails.id,
					subscriptionName: apiSubscriptionDetails.name,
					licenseQuantity: apiSubscriptionDetails.licenseQuantity,
					availableLicenseQuantity: apiSubscriptionDetails.availableLicenseQuantity,
					licenseQuantities: apiSubscriptionDetails.licenseQuantities,
					availableLicenseQuantities: apiSubscriptionDetails.availableLicenseQuantities,
					ownerFirstName: apiSubscriptionDetails.ownerFirstName,
					ownerLastName: apiSubscriptionDetails.ownerLastName,
					ownerId: apiSubscriptionDetails.ownerId
			  }
			: null;
		dispatch(
			getSubscriptionAction({
				subscriptionDetails,
				subscriptionUsersById,
				isNonSubscriber,
				hasMetadataDefinition
			})
		);
		if (
			apiSubscriptionDetails?.organizationAccessRights &&
			apiSubscriptionDetails.name === SubscriptionName.LedidiEnterprise
		)
			dispatch(
				setOrganizationAccessRightsAction({
					organizationAccessRights: apiSubscriptionDetails?.organizationAccessRights
				})
			);
	} catch (e: any) {
		const errorMessage = getMessageFromError(e);
		activity.error({
			error: errorMessage,
			toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
		});
	} finally {
		activity.end();
	}
};

export const addSubscriptionUserAction = (
	payload: ActionPayload<AddSubscriptionUserAction>
): AddSubscriptionUserAction => ({
	type: ActionTypes.ADD_SUBSCRIPTION_USER,
	payload
});

const cancelUserSubscriptionInvitationAction = (
	payload: ActionPayload<CancelUserSubscriptionInvitationAction>
): CancelUserSubscriptionInvitationAction => ({
	type: ActionTypes.CANCEL_SUBSCRIPTION_USER_INVITATION,
	payload
});

export const cancelUserSubscriptionInvitation =
	(userId: string, licenseModel: UserLicenseModel): Thunk =>
	async (dispatch, _, context) => {
		const activity = createActivity({
			type: ActionTypes.CANCEL_SUBSCRIPTION_USER_INVITATION,
			dispatch
		});

		try {
			activity.begin();

			await context.api.account.subscription().cancelUserSubscriptionInvitation({ userId });

			dispatch(
				cancelUserSubscriptionInvitationAction({ userId, licenseModel: licenseModel })
			);
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			activity.error({
				error: errorMessage,
				toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
			});
		} finally {
			activity.end();
		}
	};

const removeSubscriptionUserAction = (
	payload: ActionPayload<RemoveSubscriptionUserAction>
): RemoveSubscriptionUserAction => ({
	type: ActionTypes.REMOVE_SUBSCRIPTION_USER,
	payload
});

export const removeSubscriptionUser =
	(userId: string, licenseModel: UserLicenseModel): Thunk =>
	async (dispatch, _, context) => {
		const activity = createActivity({
			type: ActionTypes.REMOVE_SUBSCRIPTION_USER,
			dispatch
		});

		try {
			activity.begin();

			await context.api.account.subscription().removeSubscriptionUser(userId);

			dispatch(removeSubscriptionUserAction({ userId, licenseModel: licenseModel }));
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			activity.error({
				error: errorMessage,
				toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
			});
		} finally {
			activity.end();
		}
	};

const changeUserLicenseModelAction = (
	payload: ActionPayload<ChangeUserLicenseModelAction>
): ChangeUserLicenseModelAction => ({
	type: ActionTypes.CHANGE_USER_LICENSE_MODEL,
	payload
});

export const changeUserLicenseModel =
	(payload: ActionPayload<ChangeUserLicenseModelAction>): Thunk =>
	async (dispatch, _, context) => {
		const activity = createActivity({
			type: ActionTypes.CHANGE_USER_LICENSE_MODEL,
			dispatch
		});

		try {
			activity.begin();

			const { userId, licenseModel } = payload;

			await context.api.account.subscription().changeUserLicenseModel(userId, licenseModel);

			dispatch(changeUserLicenseModelAction(payload));
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			activity.error({
				error: errorMessage,
				toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
			});
		} finally {
			activity.end();
		}
	};

export const getSubscriptionApiKeyAction = (
	payload: ActionPayload<GetSubscriptionApiKeyAction>
): GetSubscriptionApiKeyAction => ({
	type: ActionTypes.GET_SUBSCRIPTION_API_KEY,
	payload
});

export const getSubscriptionApiKey = (): Thunk => async (dispatch, _, context) => {
	const activity = createActivity({
		type: ActionTypes.GET_SUBSCRIPTION_API_KEY,
		dispatch
	});

	try {
		activity.begin();

		const apiKey = await context.api.account.subscription().getSubscriptionApiKey();

		dispatch(getSubscriptionApiKeyAction({ apiKey }));
	} catch (e: any) {
		const errorMessage = getMessageFromError(e);
		activity.error({
			error: errorMessage,
			toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
		});
	} finally {
		activity.end();
	}
};

export const getLicenseModelsAction = (
	payload: ActionPayload<GetLicenseModelsAction>
): GetLicenseModelsAction => ({
	type: ActionTypes.GET_LICENSE_MODELS,
	payload
});

export const getLicenseModels = (): Thunk => async (dispatch, _, context) => {
	const activity = createActivity({
		type: ActionTypes.GET_LICENSE_MODELS,
		dispatch
	});

	try {
		activity.begin();

		const apiLicenseModels = await context.api.account.subscription().getLicenseModels();

		// TODO: BE will send more data in the Bundle version including licenseModelsMetadataById
		const licenseModels: LicenseModels = {
			allLicenseModels: apiLicenseModels as UserLicenseModel[],
			licenseModelsMetadataById: {}
			// For Bundle Subscription development
			// allLicenseModels: [
			// 	UserLicenseModel.Full,
			// 	UserLicenseModel.Collaborator,
			// 	UserLicenseModel.OneOwnedOneShared,
			// 	UserLicenseModel.Trial
			// ],
			// licenseModelsMetadataById: {
			// 	'ledidi-prime-license-EUR-Monthly': {
			// 		currency: LedidiPlanCurrency.EUR,
			// 		licenseModel: UserLicenseModel.Full,
			// 		price: 70,
			// 		billingUnit: BillingPeriodUnit.Month
			// 	},
			// 	'ledidi-prime-license-EUR-Yearly': {
			// 		currency: LedidiPlanCurrency.EUR,
			// 		licenseModel: UserLicenseModel.Full,
			// 		price: 840,
			// 		billingUnit: BillingPeriodUnit.Year
			// 	},
			// 	'ledidi-prime-license-NOK-Monthly': {
			// 		currency: LedidiPlanCurrency.NOK,
			// 		licenseModel: UserLicenseModel.Full,
			// 		price: 840,
			// 		billingUnit: BillingPeriodUnit.Month
			// 	},
			// 	'ledidi-prime-license-NOK-Yearly': {
			// 		currency: LedidiPlanCurrency.NOK,
			// 		licenseModel: UserLicenseModel.Full,
			// 		price: 10080,
			// 		billingUnit: BillingPeriodUnit.Year
			// 	},
			// 	'ledidi-collaborator-license-EUR-Monthly': {
			// 		currency: LedidiPlanCurrency.EUR,
			// 		licenseModel: UserLicenseModel.Collaborator,
			// 		price: 40,
			// 		billingUnit: BillingPeriodUnit.Month
			// 	},
			// 	'ledidi-collaborator-license-EUR-Yearly': {
			// 		currency: LedidiPlanCurrency.EUR,
			// 		licenseModel: UserLicenseModel.Collaborator,
			// 		price: 480,
			// 		billingUnit: BillingPeriodUnit.Year
			// 	},
			// 	'ledidi-collaborator-license-NOK-Monthly': {
			// 		currency: LedidiPlanCurrency.NOK,
			// 		licenseModel: UserLicenseModel.Collaborator,
			// 		price: 480,
			// 		billingUnit: BillingPeriodUnit.Month
			// 	},
			// 	'ledidi-collaborator-license-NOK-Yearly': {
			// 		currency: LedidiPlanCurrency.NOK,
			// 		licenseModel: UserLicenseModel.Collaborator,
			// 		price: 5760,
			// 		billingUnit: BillingPeriodUnit.Year
			// 	}
			// }
		};
		dispatch(getLicenseModelsAction({ licenseModels: licenseModels }));
	} catch (e: any) {
		const errorMessage = getMessageFromError(e);
		activity.error({
			error: errorMessage,
			toast: { display: errorMessage !== unknownErrorMessage, message: errorMessage }
		});
	} finally {
		activity.end();
	}
};

export const setSubscriptionUsersSearchTerm = (
	payload: ActionPayload<SetSubscriptionUsersSearchTermAction>
): SetSubscriptionUsersSearchTermAction => ({
	type: ActionTypes.SET_SUBSCRIPTION_USERS_SEARCH_TERM,
	payload
});
